import { useMemo, useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { cn } from "@/lib/utils";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { AlertCircle, Loader2, Plus, User } from "lucide-react";
import { useTickets, useTicketKpis, type TicketFilters } from "@/features/tickets/api";
import { TicketPriorityBadge, TicketSeverityBadge, TicketSourceBadge, TicketStatusBadge, TicketSlaStatusBadge, TicketSlaBadge } from "@/features/tickets/badges";
import { CrossDepartmentBadge } from "@/features/breakdown-report/CrossDepartmentBadge";
import { TICKET_PRIORITIES, TICKET_SEVERITIES, TICKET_SOURCES, TICKET_SLA_STATUSES, TICKET_STATUSES, TICKET_TYPES } from "@/features/tickets/types";
import { formatDate, formatDateTime } from "@/features/assets/format";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { KpiFilterCard } from "@/components/ui/kpi-filter-card";
import { ListExportButtons } from "@/features/reports/ListExportButtons";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { canExportMaintenanceReports, canCreateTicket, type AppRole } from "@/lib/roles";

import { useBranch } from "@/context/BranchContext";

type TicketListMode = "reporter" | "resolver" | "manager";

function ticketListModeFor(me: ReturnType<typeof useCurrentUser>["data"]): TicketListMode {
  const roles = me?.roles ?? [];
  const perms = me?.permissions ?? [];
  const managerRoles: AppRole[] = ["super_admin", "biomed_head", "facility_manager", "management"];
  const resolverRoles: AppRole[] = ["biomed_engineer", "quality"];
  
  if (roles.some((r) => managerRoles.includes(r))) return "manager";
  if (perms.some((p) => p === "assign_ticket" || p === "update_ticket_status")) return "manager";
  
  if (roles.some((r) => resolverRoles.includes(r))) return "resolver";
  if (perms.some((p) => p === "update_ticket_status")) return "resolver";
  
  return "reporter";
}

export const Route = createFileRoute("/_authenticated/tickets/")({
  component: TicketsPage,
  validateSearch: (s: Record<string, unknown>) => ({ filter: s.filter as string | undefined }),
});

function TicketsPage() {
  const navigate = useNavigate();
  const { data: me } = useCurrentUser();
  const canExport = canExportMaintenanceReports(me?.roles);
  const canCreate = canCreateTicket(me?.roles);
  const { filter } = Route.useSearch();
  const [filters, setFilters] = useState<TicketFilters>({});
  const [kpiFilter, setKpiFilter] = useState<string | null>(filter === "sla_breached" ? "sla_breached" : null);
  
  const { currentBranchId, lockedBranchId } = useBranch();
  const effectiveBranchId = lockedBranchId ?? currentBranchId ?? null;

  const listMode = ticketListModeFor(me);
  const isReporterList = listMode === "reporter";

  const effectiveFilters = useMemo<TicketFilters>(() => {
    const f: TicketFilters = { ...filters, currentUserId: me?.profile?.id ?? null, branchId: effectiveBranchId };
    if (kpiFilter === "open")           f.open = true;
    else if (kpiFilter === "in_progress")    f.status = "In Progress";
    else if (kpiFilter === "awaiting_spare") f.status = "Awaiting Spare";
    else if (kpiFilter === "awaiting_vendor")f.status = "Awaiting Vendor";
    else if (kpiFilter === "resolved")       f.status = "Resolved";
    else if (kpiFilter === "sla_breached")   f.slaBreachedOnly = true;
    return f;
  }, [filters, kpiFilter, me?.profile?.id, effectiveBranchId]);


  const { data, isLoading, error } = useTickets(effectiveFilters);
  const { data: kpi } = useTicketKpis(effectiveBranchId);

  const kpis: { key: string; label: string; value: number }[] = [
    { key: "open",           label: "Open",           value: kpi?.open ?? 0 },
    { key: "in_progress",    label: "In Progress",    value: kpi?.by_status?.["In Progress"] ?? 0 },
    { key: "awaiting_spare", label: "Awaiting Spare", value: kpi?.awaiting_spare ?? 0 },
    { key: "awaiting_vendor",label: "Awaiting Vendor",value: kpi?.awaiting_vendor ?? 0 },
    { key: "resolved",       label: "Resolved",       value: kpi?.resolved ?? 0 },
    { key: "sla_breached",   label: "SLA Breached",   value: kpi?.sla_breached ?? 0 },
  ];

  const rows = data ?? [];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Tickets</h1>
          <p className="text-sm text-muted-foreground">Unified issue reporting across QR, Manual, Asset Detail and Cross-Department.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <ListExportButtons
            rows={() =>
              rows.map((t) => ({
                "Ticket #":          t.ticket_no,
                "Title":             t.issue_title,
                "Type":              t.ticket_type,
                "Priority":          t.priority,
                "Severity":          t.severity,
                "Status":            t.status,
                "Source":            t.source,
                "Asset Code":        t.asset_code ?? "",
                "Asset Name":        t.asset_name ?? "",
                "Reporter":          t.reported_by_name ?? "",
                "Asset Department":  t.asset_department_name ?? "",
                "Assigned To":       t.assigned_to_name ?? "",
                "Assigned Vendor":   t.assigned_vendor_name ?? "",
                "Cross-Department":  t.is_cross_department_report ? "Yes" : "No",
                "Created":           t.created_at,
              }))
            }
            filename="Ticket_Register"
            sheetName="Tickets"
            disabled={isLoading || rows.length === 0}
            canExport={canExport}
          />
          {canCreate && (
            <Button onClick={() => navigate({ to: "/tickets/new" })}><Plus className="mr-1 h-4 w-4" />Create Ticket</Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        {kpis.map((k) => (
          <KpiFilterCard
            key={k.key}
            active={kpiFilter === k.key}
            onSelect={() => setKpiFilter((cur) => (cur === k.key ? null : k.key))}
            ariaLabel={`Filter tickets by ${k.label}`}
          >
            <CardContent className="p-4">
              <div className="text-xs text-muted-foreground">{k.label}</div>
              <div className="text-2xl font-semibold">{k.value}</div>
            </CardContent>
          </KpiFilterCard>
        ))}
      </div>

      {kpiFilter && (
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <span>Filtered by KPI card.</span>
          <Button size="sm" variant="ghost" onClick={() => setKpiFilter(null)}>Clear</Button>
        </div>
      )}

      <Card>
        <CardHeader className="pb-3"><CardTitle className="text-base">Filters</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <Input placeholder="Search ticket no / title…" value={filters.search ?? ""} onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))} />
          <Select value={filters.status ?? "__all"} onValueChange={(v) => setFilters((f) => ({ ...f, status: v === "__all" ? null : v as never }))}>
            <SelectTrigger><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent><SelectItem value="__all">All statuses</SelectItem>{TICKET_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={filters.priority ?? "__all"} onValueChange={(v) => setFilters((f) => ({ ...f, priority: v === "__all" ? null : v as never }))}>
            <SelectTrigger><SelectValue placeholder="Priority" /></SelectTrigger>
            <SelectContent><SelectItem value="__all">All priorities</SelectItem>{TICKET_PRIORITIES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={filters.ticketType ?? "__all"} onValueChange={(v) => setFilters((f) => ({ ...f, ticketType: v === "__all" ? null : v as never }))}>
            <SelectTrigger><SelectValue placeholder="Ticket Type" /></SelectTrigger>
            <SelectContent><SelectItem value="__all">All types</SelectItem>{TICKET_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={filters.slaStatus ?? "__all"} onValueChange={(v) => setFilters((f) => ({ ...f, slaStatus: v === "__all" ? null : (v as never) }))}>
            <SelectTrigger><SelectValue placeholder="SLA" /></SelectTrigger>
            <SelectContent><SelectItem value="__all">All SLA</SelectItem>{TICKET_SLA_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
          <Input type="date" value={filters.dateFrom ?? ""} onChange={(e) => setFilters((f) => ({ ...f, dateFrom: e.target.value || null }))} />
          <Input type="date" value={filters.dateTo ?? ""} onChange={(e) => setFilters((f) => ({ ...f, dateTo: e.target.value || null }))} />
          <label className="flex items-center gap-2 text-sm">
            <Checkbox checked={!!filters.mineOnly} onCheckedChange={(v) => setFilters((f) => ({ ...f, mineOnly: !!v }))} />
            <span>My Tickets</span>
          </label>
          <label className="flex items-center gap-2 text-sm">
            <Checkbox checked={!!filters.crossDepartmentOnly} onCheckedChange={(v) => setFilters((f) => ({ ...f, crossDepartmentOnly: !!v }))} />
            <span>Cross-Department only</span>
          </label>
          <div className="md:col-span-2 flex items-center justify-end">
            <Button size="sm" variant="ghost" onClick={() => setFilters({})}>Reset filters</Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="grid place-items-center p-10"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
          ) : error ? (
            <div className="p-4">
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{(error as Error).message || "Unable to load tickets."}</AlertDescription>
              </Alert>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Ticket #</TableHead>
                  <TableHead>Title</TableHead>
                  <TableHead>Asset</TableHead>
                  {!isReporterList && <TableHead>Type</TableHead>}
                  <TableHead>Priority</TableHead>
                  <TableHead>Status</TableHead>
                  {!isReporterList && <TableHead>SLA</TableHead>}
                  {listMode === "manager" && <TableHead>Source</TableHead>}
                  {listMode === "manager" && <TableHead>Reporter</TableHead>}
                  {listMode !== "reporter" && <TableHead>Branch</TableHead>}
                  {listMode !== "reporter" && <TableHead>Department</TableHead>}
                  {listMode !== "reporter" && <TableHead>Assigned To</TableHead>}
                  {listMode === "manager" && <TableHead>Work Order</TableHead>}
                  <TableHead>Created</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((t) => {
                  const isAssignedToMe = !!me?.user?.id && (t.assigned_user_id === me.user.id || (t as any).assigned_user === me.user.id);
                  return (
                  <TableRow key={t.id} className={cn("cursor-pointer", isAssignedToMe && "bg-blue-50/50 hover:bg-blue-50 dark:bg-blue-900/10 dark:hover:bg-blue-900/20")} onClick={() => navigate({ to: "/tickets/$ticketId", params: { ticketId: t.id } })}>
                    <TableCell className="font-mono text-xs">
                      {t.ticket_no}
                    </TableCell>
                    <TableCell className="text-sm">
                      <div className="flex items-center gap-2">
                        <span className="truncate">{t.issue_title}</span>
                        {t.is_cross_department_report && !isReporterList && <CrossDepartmentBadge compact />}
                      </div>
                    </TableCell>
                    <TableCell className="text-sm">
                      {t.asset_code ? (
                        <>
                          <div>{t.asset_name}</div>
                          <div className="font-mono text-[11px] text-muted-foreground">{t.asset_code}</div>
                        </>
                      ) : <span className="text-muted-foreground">—</span>}
                    </TableCell>
                    {!isReporterList && <TableCell className="text-xs">{t.ticket_type}</TableCell>}
                    <TableCell><TicketPriorityBadge value={t.priority} /></TableCell>
                    <TableCell><TicketStatusBadge value={t.status} /></TableCell>
                    {!isReporterList && <TableCell><TicketSlaBadge ticket={t} /></TableCell>}
                    {listMode === "manager" && <TableCell><TicketSourceBadge value={t.source as any} /></TableCell>}
                    {listMode === "manager" && <TableCell className="text-xs">{t.reported_by_name ?? "—"}</TableCell>}
                    {listMode !== "reporter" && <TableCell className="text-xs">{t.branch_name ?? "—"}</TableCell>}
                    {listMode !== "reporter" && <TableCell className="text-xs">{t.asset_department_name ?? "—"}</TableCell>}
                    {listMode !== "reporter" && (
                      <TableCell className="text-xs">
                        {isAssignedToMe ? (
                          <span className="inline-flex items-center gap-1.5 font-medium text-blue-700 dark:text-blue-400">
                            <User className="h-3.5 w-3.5" />
                            {t.assigned_to_name} (Me)
                          </span>
                        ) : (
                          t.assigned_to_name ?? t.assigned_vendor_name ?? "—"
                        )}
                      </TableCell>
                    )}
                    {listMode === "manager" && (
                      <TableCell className="text-xs">
                        {t.active_work_order_number ? (
                          <Link
                            to="/work-orders/$woId"
                            params={{ woId: t.active_work_order! }}
                            className="font-mono text-primary hover:underline"
                            onClick={(e) => e.stopPropagation()}
                          >{t.active_work_order_number}</Link>
                        ) : <span className="text-muted-foreground">Not created</span>}
                      </TableCell>
                    )}
                    <TableCell className="text-xs whitespace-nowrap">{formatDateTime(t.created_at)}</TableCell>
                  </TableRow>
                  );
                })}
                {rows.length === 0 && (
                  <TableRow><TableCell colSpan={13} className="text-center py-8 text-sm text-muted-foreground">No tickets found.</TableCell></TableRow>
                )}
              </TableBody>
            </Table>

          )}
        </CardContent>
      </Card>
      {/* Prevent unused-import TS warnings for label helper */}
      <span className="hidden"><Label>_</Label></span>
    </div>
  );
}
